import ServiceLevel from './ServiceLevel';

interface JSONProviderAccount {
  provider_name: string;
  provider_token: string;
  is_shippo_account: boolean;
  is_test: boolean;
  nickname: string;
  object_id: string;
  provider_image_75: string;
  provider_image_200: string;
  is_manifestable: boolean;
  service_levels: ServiceLevel[];
}

interface ProviderAccount {
  providerName: string;
  providerToken: string;
  isShippoAccount: boolean;
  isTest: boolean;
  nickname: string;
  objectId: string;
  providerImage75: string;
  providerImage200: string;
  isManifestable: boolean;
  serviceLevels: ServiceLevel[];
  serviceLevelsByToken: {
    [token: string]: ServiceLevel;
  };
  visibleServiceLevels: ServiceLevel[];
  allServiceLevels: ServiceLevel[];
}

interface MappedProviderAccount {
  [token: string]: ProviderAccount[];
}

interface ObjectIdProviderAccountMap {
  [objectId: string]: ProviderAccount;
}

function fromJSON(jsonProviderAccounts: JSONProviderAccount[]): ProviderAccount[] {
  return jsonProviderAccounts
    .sort((a: JSONProviderAccount, b: JSONProviderAccount) => {
      if (!a?.provider_name || !b?.provider_name) {
        return 0;
      }
      return a.provider_name.localeCompare(b.provider_name);
    })
    .map((jsonProviderAccount: JSONProviderAccount): ProviderAccount => {
      const serviceLevelsByToken: Record<string, ServiceLevel> = {};
      for (const serviceLevel of jsonProviderAccount.service_levels) {
        serviceLevelsByToken[serviceLevel.token] = serviceLevel;
      }

      // create the list of all Service Levels, including any "child" Service Levels (country-specific variations, for instance)
      // "Child" Service Levels are rarely needed when showing Service Levels to Users, but may be necessary when pairing Rates to Service Levels
      // as Rates *can* have these Service Levels
      const allServiceLevels = jsonProviderAccount.service_levels
        .sort((a, b) => {
          // Sort the Provider Accounts by name
          if (!a?.name || !b?.name) {
            return 0;
          }
          return a.name.localeCompare(b.name);
        })
        .map((serviceLevel) => {
          // Populate the Provider image on the Service Level using the data from the ProviderAccount
          return {
            ...serviceLevel,
            provider_name: jsonProviderAccount.provider_name,
            provider_image_75: jsonProviderAccount.provider_image_75,
            provider_image_200: jsonProviderAccount.provider_image_200,
          };
        });

      // create the list of Service Levels without any "child" Service Levels (see above)
      const serviceLevels = allServiceLevels.filter(
        (serviceLevel) => !serviceLevel?.parent_servicelevel,
      );

      // create the list of "visible" Service Levels (where all flagged "is_hidden" are filtered out and "child" service levels are removed)
      // In most case, this is the list that should be used if showing possible Service Levels to a user
      const visibleServiceLevels = serviceLevels.filter((serviceLevel) => !serviceLevel?.is_hidden);

      return {
        providerName: jsonProviderAccount.provider_name,
        providerToken: jsonProviderAccount.provider_token,
        objectId: jsonProviderAccount.object_id,
        isShippoAccount: jsonProviderAccount.is_shippo_account,
        isTest: jsonProviderAccount.is_test,
        nickname: jsonProviderAccount.nickname,
        providerImage75: jsonProviderAccount.provider_image_75,
        providerImage200: jsonProviderAccount.provider_image_200,
        isManifestable: jsonProviderAccount.is_manifestable,
        serviceLevels,
        visibleServiceLevels,
        allServiceLevels,
        serviceLevelsByToken,
      };
    });
}

export { JSONProviderAccount, fromJSON, MappedProviderAccount, ObjectIdProviderAccountMap };

export default ProviderAccount;
